/**
 * Drawer Configuration Management Hook
 * 
 * Manages drawer configuration state and provides methods for
 * retrieving and updating drawer configurations.
 */

import * as React from "react";
import type { DrawerConfig, GlobalDrawerId } from "../types.js";
import { mergeDrawerConfig, getDefaultDrawerConfig } from "../utils/drawerConfig.js";

/**
 * Hook for managing drawer configurations
 * 
 * @returns Object with config management methods
 */
export function useDrawerConfig() {
  const [drawerConfigs, setDrawerConfigs] = React.useState<Map<string, DrawerConfig>>(new Map());

  const getDrawerConfig = React.useCallback(
    (id: GlobalDrawerId): DrawerConfig | null => {
      return drawerConfigs.get(id) || null;
    },
    [drawerConfigs]
  );

  const setDrawerConfigValue = React.useCallback(
    (id: GlobalDrawerId, config: Partial<DrawerConfig>) => {
      setDrawerConfigs((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(id);
        const merged = mergeDrawerConfig(id, existing, config);
        newMap.set(id, merged);
        return newMap;
      });
    },
    []
  );

  const updateDrawerConfig = React.useCallback(
    (id: GlobalDrawerId, config?: Partial<DrawerConfig>) => {
      if (config) {
        setDrawerConfigValue(id, config);
      }
    },
    [setDrawerConfigValue]
  );

  return React.useMemo(
    () => ({
      getDrawerConfig,
      setDrawerConfig: setDrawerConfigValue,
      updateDrawerConfig,
    }),
    [getDrawerConfig, setDrawerConfigValue, updateDrawerConfig]
  );
}
